import { faDownload, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PrepareFiles } from '../../Framework/Components/Forms/File/UploadFileHelpers';
import { UploadBlockType, UploadFileDetailsType } from '../../Framework/Components/Forms/File/UploadFileTypes';
import { ColourMod, FlexAlign, FlexGap, FlexJustify } from '../../Framework/Data/Enums/LayoutEnums';
import StackVertical from '../../Framework/Components/Wrappers/StackVertical';
import { GetReadableFileSize } from '../../Framework/Constants/Helpers';
import Button from '../../Framework/Components/Buttons/Button';
import AlertBanner from '../../Framework/Components/AlertBanner/AlertBanner';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faPaperPlane } from '@fortawesome/pro-solid-svg-icons/faPaperPlane';
import { faUpload } from '@fortawesome/pro-solid-svg-icons/faUpload';
import Lottie from 'lottie-react';
import sendStart from '../Animations/send-start.json';
import sendProcess from '../Animations/send-process.json';
import sendEnd from '../Animations/send-end.json';
import { useToasts } from '../../Framework/Providers/ToastsProvider';
import { faBug } from '@fortawesome/pro-solid-svg-icons/faBug';
import ToggleSwitch from '../../Framework/Components/Forms/Toggle/ToggleSwitch';
import { ToggleType } from '../../Framework/Data/Enums/Generic';
import StackHorizontal from '../../Framework/Components/Wrappers/StackHorizontal';
import { useSendMail } from '../../Providers/SendMailProvider';
import { SendDataProcess } from '../Sends/StandaloneSendForm';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { FileResponseType, sendMail, uploadFile } from '../../Api/SendMailEndpoints';

interface MultiSendsUploadEntryType extends UploadFileDetailsType {
	formData: any;
	file: SendsFileType;
	index: number;
	addCallback: () => void;
	createdCallback: (trackingId: string) => void;
	errorCallback: (error: string) => void;
	startCallback: () => void;
}

function MultiSendsUploadEntry(props: MultiSendsUploadEntryType) {
	const delayTime = 500;

	const [fileInvalid, setFileInvalid] = useState<boolean>(props.file.invalid);
	const [fileError, setFileError] = useState<boolean>(false);
	const [uploading, setUploading] = useState<boolean>(false);
	const [complete, setComplete] = useState<boolean>(props.file.completed);
	const [trackingId, setTrackingId] = useState<string>(props.file.trackingId || '');

	const [mouseOverList, setMouseOverList] = useState<boolean>(false);

	const entryRef = useRef<HTMLDivElement>(null);

	const blockedRef = useRef(false);

	const Start = useCallback(() => {
		setUploading(true);
		props.startCallback();

		if (props.file.invalid) {
			setTimeout(() => {
				setFileInvalid(true);
				setFileError(true);
				setComplete(false);
				setUploading(false);
				props.errorCallback(`${props.file.name} is invalid and will not be processed`);
			}, delayTime);
		} else {
			/*			setTimeout(() => {
				setTrackingId('sdkfjhs dkjfhsdkjh');
				setComplete(true);
				setUploading(false);
				props.createdCallback('sdkfjhs dkjfhsdkjh');
			}, delayTime);*/
			uploadFile(props.file)
				.then((r: FileResponseType) => {
					sendMail({
						postage: props.formData.postage,
						address: props.formData.address,
						sendsPageId: props.formData.sendsPageId,
						file: r.fileUuid,
					})
						.then((s) => {
							setTimeout(() => {
								setTrackingId(s.trackingId);
								setComplete(true);
								setUploading(false);
								props.createdCallback(s.trackingId);
							}, delayTime);
						})
						.catch((e) => {
							setTimeout(() => {
								setFileInvalid(true);
								setFileError(true);
								setComplete(false);
								setUploading(false);
								props.errorCallback(e.response?.data?.error || e.toString());
							}, delayTime);
						});
				})
				.catch((ex) => {
					setTimeout(() => {
						setFileInvalid(true);
						setFileError(true);
						setComplete(false);
						setUploading(false);
						props.errorCallback(ex.response?.data?.error || ex.toString());
					}, delayTime);
				});
		}
	}, [props, fileError, uploading]);

	const HandleMousePosition = (e: MouseEvent) => {
		const el = document.elementFromPoint(e.clientX, e.clientY);
		setMouseOverList(el?.closest('.multi-sends-files-wrapper') !== null);
	};

	useEffect(() => {
		if (!mouseOverList && uploading && entryRef.current) {
			entryRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [uploading, mouseOverList]);

	useEffect(() => {
		if (!blockedRef.current && props.file.canUpload && !props.file.completed) {
			blockedRef.current = true;
			Start();
		}

		if (!props.file.skipUpload && !fileInvalid && fileError) {
			setFileError(false);
			Start();
		}
	}, [Start, props, fileError, fileInvalid, uploading]);

	useEffect(() => {
		document.addEventListener('mousemove', HandleMousePosition);
		return () => document.removeEventListener('mousemove', HandleMousePosition);
	}, []);

	return (
		<div
			className={`upload-file-entry upload-file-entry-bulk ${props.file.skipUpload ? ColourMod.Secondary : ''} ${
				fileError ? ColourMod.Alert : ''
			} ${complete ? ColourMod.Success : ''}`}
			aria-label={`${props.file.name} File Entry`}
			ref={entryRef}
		>
			<div className={'multi-send-entry-icon'}>
				{complete && <FontAwesomeIcon icon={faCheckCircle} fixedWidth size={'2xl'} />}
				{uploading && !fileError && <FontAwesomeIcon icon={faSpinner} fixedWidth spin size={'2xl'} />}
				{!complete && !uploading && !fileError && <FontAwesomeIcon icon={faPaperPlane} fixedWidth />}
				{fileError && !fileInvalid && <FontAwesomeIcon icon={faExclamationTriangle} fixedWidth size={'2xl'} />}
				{fileInvalid && <FontAwesomeIcon icon={faBug} fixedWidth size={'2xl'} />}
			</div>

			<StackVertical align={FlexAlign.Start} extraClass={'multi-send-meta-container'}>
				<div className={'upload-file-entry-name'}>{props.file.name}</div>
				<span className={'upload-file-entry-size'}>{GetReadableFileSize(props.file.size)}</span>
			</StackVertical>

			<div className={'upload-file-entry-actions'}>
				{trackingId ? (
					<StackVertical align={FlexAlign.Start}>
						<strong>Created</strong>
						<a
							href={`${process.env.REACT_APP_CLIENT_HOST}/Mail#/mail/${trackingId}`}
							target={'_blank'}
							rel='noreferrer'
						>
							{trackingId}
						</a>
					</StackVertical>
				) : (
					<>
						{!props.file.skipUpload && !uploading && !fileError && !fileInvalid && (
							<Button
								title={`Remove ${props.file.name}`}
								icon={<FontAwesomeIcon icon={faTrash} fixedWidth={true} />}
								iconOnly
								onClick={() => props.removeCallback()}
								extraClass={`upload-file-entry-action upload-file-entry-remove ml-a`}
								colour={ColourMod.Alert}
							/>
						)}

						{props.file.skipUpload && !uploading && !fileError && !fileInvalid && (
							<Button
								title={`Add ${props.file.name}`}
								icon={<FontAwesomeIcon icon={faUpload} fixedWidth={true} />}
								iconOnly
								onClick={() => props.addCallback()}
								extraClass={`upload-file-entry-action upload-file-entry-remove ml-a`}
								colour={ColourMod.Primary}
							/>
						)}
					</>
				)}

				{fileError && !fileInvalid && (
					<Button
						title={`Retry ${props.file.name}`}
						icon={<FontAwesomeIcon icon={faRedo} fixedWidth={true} />}
						iconOnly
						onClick={() => props.addCallback()}
						extraClass={`upload-file-entry-action upload-file-entry-retry`}
						colour={ColourMod.Alert}
					/>
				)}

				{fileInvalid && (
					<StackVertical align={FlexAlign.Start}>
						<strong>Invalid File</strong>
					</StackVertical>
				)}
			</div>
		</div>
	);
}

interface MultiSendsUploadType extends Omit<UploadBlockType, 'onChange'> {
	sendData: SendDataProcess;
	uploadStatus?: any;
	closeCallback: () => void;
	queueStatusCallback: (state: boolean) => void;
}

interface SendsFileType extends File {
	completed: boolean;
	canUpload: boolean;
	skipUpload: boolean;
	invalid: boolean;
	trackingId?: string;
	completedTime?: Date;
}

function MultiSendsUpload(props: MultiSendsUploadType) {
	const { sendPageData } = useSendMail();
	const { addToast } = useToasts();

	const [startAnimationComplete, setStartAnimationComplete] = useState<boolean>(false);
	const [processAnimationComplete, setProcessAnimationComplete] = useState<boolean>(false);
	const [endAnimationComplete, setEndAnimationComplete] = useState<boolean>(false);

	const [queueProcessing, setQueueProcessing] = useState<boolean>(false);

	const [files, setFiles] = useState<Array<SendsFileType>>([]);
	const [maxSize] = useState<any>(props.maxSizeInBytes || 52428800);
	const [maxFiles] = useState<any>(props.maxFiles || 1000);

	const [queueComplete, setQueueComplete] = useState<boolean>(false);

	const [playSound, setPlaySound] = useState<boolean>(false);

	const acceptsFileExtension = ['.pdf'];
	const acceptsMimeType = props.acceptsMimeType;

	const inputRef = useRef<HTMLInputElement>(null);

	const filesAttachmentsRef = useRef<Array<any>>(files);
	const queueCompleteRef = useRef<boolean>(queueComplete);

	const multiSendsScroller = useRef<HTMLDivElement>(null);

	const PlayNotification = () => {
		if (playSound) {
			const audio = new Audio(`${process.env.PUBLIC_URL}/notification.mp3`);
			audio.play();
		}

		document.title = sendPageData?.title
			? `${sendPageData.title} | UK Postbox`
			: 'Loading Sends Profile | UK Postbox';
	};

	const SortSkippedFiles = () => {
		// @ts-ignore
		let newFiles = files.toSorted((a, b) => !!a.invalid - !!b.invalid);
		// @ts-ignore
		newFiles = newFiles.toSorted((a, b) => !!a.skipUpload - !!b.skipUpload);

		filesAttachmentsRef.current = newFiles;
		setFiles(newFiles);
	};

	const ChangeQueueStatus = (state: boolean) => {
		setQueueProcessing(state);

		if (processAnimationComplete) {
			setProcessAnimationComplete(false);
			setEndAnimationComplete(false);
		}

		if (state) {
			SortSkippedFiles();
		}
	};

	const HandleFileChange = useCallback(
		(filesChanged: FileList | Array<File> | null, appending = false) => {
			const preparedFiles = PrepareFiles(
				{
					existingFiles: appending ? filesAttachmentsRef.current : [],
					selectedFiles: filesChanged || [],
					singleFile: props.singleFileOnly || false,
					mergeFiles: false,
					maxFiles,
					maxSize,
					acceptsFileExtension,
					acceptsMimeType,
					onChangeCallback: (newFiles) => null,
				},
				true
			);

			const sendsPreparedFiles = [...(preparedFiles.files as Array<SendsFileType>)];

			sendsPreparedFiles.forEach((entry, index, object) => {
				if (entry.canUpload === undefined) {
					entry.canUpload = index === 0;
				}

				entry.completed = entry.completed || false;
			});

			if (queueCompleteRef.current) {
				for (const entry of sendsPreparedFiles) {
					if (!entry.skipUpload && !entry.completed) {
						entry.canUpload = true;
						queueCompleteRef.current = false;
						props.queueStatusCallback(true);
						setQueueComplete(false);
						break;
					}
				}
			}

			if (preparedFiles.errors.state) {
				props.onError(preparedFiles.errors.state);
			}

			filesAttachmentsRef.current = sendsPreparedFiles;
			setFiles(sendsPreparedFiles);
		},
		[acceptsFileExtension, acceptsMimeType, addToast, maxFiles, maxSize, props]
	);

	const DragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		if (!e.currentTarget.classList.contains('drop-active')) {
			e.currentTarget.classList.add('drop-active');
		}
	};

	const DragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		if (e.currentTarget.classList.contains('drop-active')) {
			e.currentTarget.classList.remove('drop-active');
		}
	};

	const DragDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();

		if (e.dataTransfer?.files) {
			HandleFileChange(e.dataTransfer?.files, true);
		}

		if (e.currentTarget.classList.contains('drop-active')) {
			e.currentTarget.classList.remove('drop-active');
		}
	};

	const DownloadSummary = () => {
		const header = ['File Name', 'Status', 'Creation Time', 'Tracking ID'];

		const csv = [
			header.join(', '),
			...files.map(
				(row) =>
					`${row.name}, ${
						row.invalid || (row.completed && !row.completedTime && !row.trackingId)
							? 'Skipped - Invalid File'
							: row.skipUpload
							? 'Skipped - User'
							: 'Completed'
					}, ${
						row.completedTime
							? `${row.completedTime?.toLocaleDateString()} ${row.completedTime?.toLocaleTimeString()}`
							: ''
					}, ${row.trackingId ? row.trackingId : ''}`
			),
		].join('\r\n');

		const link = document.createElement('a');
		link.setAttribute('href', 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(csv));
		link.setAttribute(
			'download',
			`Sends-Summary-${new Date().toLocaleDateString().replace(/\//g, '-')}-${new Date()
				.toLocaleTimeString()
				.replace(/:/g, '-')}`
		);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const HandleFileToggle = useCallback(
		(index: number, state: boolean) => {
			const newFiles = [...filesAttachmentsRef.current];
			newFiles[index].skipUpload = state;

			HandleFileChange(newFiles);
		},
		[HandleFileChange]
	);

	const HandleInputTrigger = () => {
		if (inputRef.current) {
			inputRef.current.value = '';
			inputRef.current.click();
		}
	};

	const UpdateProcessedFiles = useCallback(
		(index: number, processedFile = true) => {
			const newFiles = [...filesAttachmentsRef.current];

			newFiles[index].completed = true;

			if (processedFile) {
				newFiles[index].completedTime = new Date();
			}

			let hasNextFile = false;

			for (const entry of newFiles) {
				if (!entry.skipUpload && !entry.completed) {
					entry.canUpload = true;
					hasNextFile = true;
					break;
				}
			}

			if (!queueCompleteRef.current) {
				document.title = `Creating Sends ${newFiles.filter((file) => file.completed).length}/${
					newFiles.filter((file) => !file.skipUpload).length
				} | UK Postbox`;
			}

			if (!hasNextFile) {
				queueCompleteRef.current = true;
				props.queueStatusCallback(false);
				setQueueComplete(true);
			}

			return newFiles;
		},
		[props]
	);

	const SendErrorCallback = useCallback(
		(error: string, index: number) => {
			addToast(error, <FontAwesomeIcon icon={faBug} />, ColourMod.Alert);

			if (filesAttachmentsRef.current.length > 0) {
				const newFiles = UpdateProcessedFiles(index, false);
				newFiles[index].invalid = true;

				HandleFileChange(newFiles);
			}
		},
		[addToast, UpdateProcessedFiles, HandleFileChange]
	);

	const SendActionedCallback = useCallback(
		(index: number, trackingId: string) => {
			if (filesAttachmentsRef.current.length > 0) {
				const newFiles = UpdateProcessedFiles(index);
				newFiles[index].trackingId = trackingId;

				HandleFileChange(newFiles);
			}
		},
		[UpdateProcessedFiles, HandleFileChange]
	);

	useEffect(() => {
		if (props.sendData.form?.file && props.sendData.form.file.length > 0 && files.length === 0) {
			HandleFileChange(props.sendData.form.file);
		}
	}, [files, props.sendData, HandleFileChange]);

	useEffect(() => {
		ChangeQueueStatus(queueComplete);
	}, [queueComplete]);

	return (
		<div className={'upload-file-wrapper multi-sends-file-wrapper'}>
			<div
				onClick={() => HandleInputTrigger()}
				onKeyDown={(e) => (e.code === 'Enter' ? HandleInputTrigger() : null)}
				tabIndex={0}
				role='button'
				aria-label={`${props.name} File Upload`}
				className={`${'upload-block'} ${props.passive ? 'upload-hoverable' : ''} multi-sends-upload-block`}
				onDragEnter={(e) => DragEnter(e)}
				onDragLeave={(e) => DragLeave(e)}
				onDragOver={(e) => e.preventDefault()}
				onDrop={(e) => DragDrop(e)}
			>
				<StackVertical gap={FlexGap.Small} extraClass={`multi-sends-progress-container`}>
					{!startAnimationComplete && (
						<StackVertical justify={FlexJustify.Center} gap={FlexGap.Large} align={FlexAlign.Center}>
							<div className={'animation-framer'}>
								<Lottie
									style={{ width: '250px', height: '250px' }}
									animationData={sendStart}
									loop={false}
									onComplete={() => setStartAnimationComplete(true)}
								/>
							</div>
							<strong>Click or drag files here to add them to the queue...</strong>
						</StackVertical>
					)}

					{startAnimationComplete && !processAnimationComplete && (
						<StackVertical justify={FlexJustify.Center} gap={FlexGap.Large} align={FlexAlign.Center}>
							<div className={'animation-framer'}>
								<Lottie
									style={{ width: '250px', height: '250px' }}
									animationData={sendProcess}
									loop={true}
									onLoopComplete={() => {
										setProcessAnimationComplete(queueProcessing);
									}}
								/>
							</div>

							<strong>Click or drag files here to add them to the queue...</strong>
						</StackVertical>
					)}

					{processAnimationComplete && (
						<StackVertical justify={FlexJustify.Center} gap={FlexGap.Large} align={FlexAlign.Center}>
							<div className={'animation-framer'}>
								<Lottie
									style={{ width: '250px', height: '250px' }}
									animationData={sendEnd}
									loop={false}
									onComplete={() => {
										PlayNotification();
										setEndAnimationComplete(true);
									}}
								/>
							</div>

							<strong>Click or drag files here to add them to the queue...</strong>
						</StackVertical>
					)}
				</StackVertical>

				<input
					ref={inputRef}
					onChange={(e) => {
						if (e.target.files) {
							HandleFileChange(e.target.files, true);
						}
					}}
					type='file'
					className={'custom-file-input'}
					name={props.name}
					multiple={!props.singleFileOnly}
					accept={props.acceptsFileExtension?.toString() || props.acceptsMimeType?.toString()}
					tabIndex={-1}
					aria-label={`${props.name} File Upload Input`}
				/>
			</div>

			<StackHorizontal gap={FlexGap.Small} align={FlexAlign.Center}>
				<div>
					<ToggleSwitch
						name={'PlaySound'}
						checked={playSound}
						label={'Play Sound on Completion'}
						type={ToggleType.Switch}
						onChange={() => setPlaySound(!playSound)}
					/>
				</div>
				<div className={'ml-a'}>
					{queueComplete && (
						<Button
							title={'Download Summary'}
							icon={<FontAwesomeIcon icon={faDownload} fixedWidth />}
							colour={ColourMod.Primary}
							onClick={() => DownloadSummary()}
						/>
					)}
				</div>
			</StackHorizontal>

			{props.componentError && props.componentError.state && (
				<AlertBanner
					title={props.componentError.title}
					content={props.componentError.message}
					colourMod={ColourMod.Alert}
				/>
			)}

			<h6 className='mb-xs'>
				{props.selectedFilesHeader
					? props.selectedFilesHeader
					: `Processed Files ${
							files.filter((file) => file.completed && !file.invalid && !file.skipUpload).length
					  }/${files.length}`}
			</h6>

			{files?.length > 0 && (
				<div className={'upload-selected-files-wrapper multi-sends-files-wrapper'} ref={multiSendsScroller}>
					<div className={'upload-file-info'}>
						{files.map((file, index) => (
							<MultiSendsUploadEntry
								key={`multiSendsFileUpload${file.name}${file.size}${file.lastModified}${file.type}${file.trackingId}${index}`}
								file={file}
								index={index}
								valid={file.size < maxSize}
								startCallback={() => null}
								createdCallback={(trackingId: string) => SendActionedCallback(index, trackingId)}
								addCallback={() => HandleFileToggle(index, false)}
								removeCallback={() => HandleFileToggle(index, true)}
								errorCallback={(error) => SendErrorCallback(error, index)}
								formData={props.sendData.form}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

export { MultiSendsUpload };
